import axios from 'axios';
import API_CONFIG from '../config/api';
import { getAuthToken } from '../helpers/authDebug';

export interface PlanInfo {
  planName: string;
  planPrice: number;
  planFeatures: string[];
  planPeriod: number;
  basePrice: number;
  discount: string | null;
}

export interface Transaction {
  _id: string;
  userId: string;
  amount: number;
  description: string;
  authority: string;
  refId?: string;
  planName: string;
  planPeriod: number;
  basePrice: number;
  discount?: string;
  status: 'pending' | 'completed' | 'failed';
  createdAt: string;
  paymentDate?: string;
}

export interface PaymentRequest {
  amount: number;
  description: string;
  callbackUrl: string;
  email: string;
  mobile: string;
  userId: string;
  planName: string;
  planPeriod: number;
  basePrice: number;
  discount: string | null;
  purchaseMonth?: string; // Format: YYYY-MM to track which month the purchase was made
  returnUrl?: string; // URL to return to after payment completion
}

export interface PaymentResponse {
  success: boolean;
  url?: string;
  authority?: string;
  transactionId?: string;
  message?: string;
  code?: number;
}

export interface VerificationResponse {
  success: boolean;
  refId?: string;
  message?: string;
}

export interface UserInfo {
  id: string;
  email: string;
  mobile: string;
}

const paymentService = {
  /**
   * Get user information
   */
  getUserInfo: async (token?: string): Promise<UserInfo> => {
    // Use provided token or get from storage if not provided
    const authToken = token || getAuthToken();
    
    if (!authToken) {
      throw new Error('No authentication token available');
    }
    
    const response = await axios.get(API_CONFIG.getUrl('users/me'), {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    });
    
    return {
      id: response.data.data._id,
      email: response.data.data.email || '',
      mobile: response.data.data.mobile || ''
    };
  },
  
  /**
   * Create a payment
   */
  createPayment: async (paymentRequest: PaymentRequest, token?: string): Promise<PaymentResponse> => {
    // Use provided token or get from storage if not provided
    const authToken = token || getAuthToken();
    
    if (!authToken) {
      throw new Error('No authentication token available');
    }

    const response = await axios.post(API_CONFIG.getUrl('payments/create'), paymentRequest, {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    });
    
    return response.data;
  },
  
  /**
   * Verify a payment
   */
  verifyPayment: async (queryParams: string, token: string): Promise<VerificationResponse> => {
    // Parse query parameters to send as POST data
    const urlParams = new URLSearchParams(queryParams);
    const paymentData = {
      Token: urlParams.get('Token'),
      Status: urlParams.get('Status') || urlParams.get('status'),
      OrderId: urlParams.get('OrderId'),
      RRN: urlParams.get('RRN'),
      TerminalNo: urlParams.get('TerminalNo'),
      Amount: urlParams.get('Amount'),
      transactionId: urlParams.get('transactionId')
    };

    const response = await axios.post(API_CONFIG.getUrl('payments/verify'), paymentData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response.data;
  },
  
  /**
   * Get user transactions
   */
  getUserTransactions: async (userId: string, token: string): Promise<Transaction[]> => {
    const response = await axios.get(API_CONFIG.getUrl(`payments/user/${userId}`), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data.success ? response.data.data : [];
  },
  
  /**
   * Get transaction by ID
   */
  getTransactionById: async (transactionId: string, token: string): Promise<Transaction | null> => {
    const response = await axios.get(API_CONFIG.getUrl(`payments/${transactionId}`), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data.success ? response.data.data : null;
  }
};

export default paymentService;
